import { msg, select } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { Button, Tooltip } from '@mui/material';
import { memo } from 'react';
import { Side } from '@/common/constants';

interface ActionButtonProps {
  disabled: boolean;
  disabledReason?: string;
  tradingEnabled: boolean;
  side: Side;
  depositButtonText?: string;
  onDepositClick?: () => void;
  onGeoBlockClick?: () => void;
  isGeoBlocked?: boolean;
  reduceOnly?: boolean;
  marketSymbol?: string;
}

const actionButtonSx = {
  height: '40px',
  whiteSpace: 'nowrap',
  fontSize: { xs: '0.8125rem', md: undefined },
};

export const ActionButton = memo(
  ({
    disabled,
    disabledReason,
    tradingEnabled,
    side,
    depositButtonText,
    onDepositClick,
    onGeoBlockClick,
    isGeoBlocked,
    reduceOnly,
    marketSymbol,
  }: ActionButtonProps) => {
    const { i18n, t } = useLingui();

    if (isGeoBlocked) {
      return (
        <Button onClick={onGeoBlockClick} fullWidth size="large" variant="contained" color="error" sx={actionButtonSx}>
          {t({ comment: 'Geo-blocked action button', message: 'Unavailable in your location' })}
        </Button>
      );
    }

    if (tradingEnabled || reduceOnly) {
      const buttonText = reduceOnly
        ? disabled
          ? t({ comment: 'Reduce only disabled button text', message: 'Reduce Only Enabled' })
          : t({ comment: 'Reduce only order button text', message: 'Place Reduce-Only Order' })
        : i18n._(
            msg({
              comment: 'Trade console button label',
              message: select(
                { side },
                {
                  long: `Long ${{ symbol: marketSymbol || '' }}`,
                  short: `Short ${{ symbol: marketSymbol || '' }}`,
                  other: `${{ symbol: marketSymbol || '' }}`,
                }
              ),
            })
          );

      const button = (
        <Button
          fullWidth
          disabled={disabled}
          size="large"
          variant="contained"
          color={side === Side.long ? 'success' : 'error'}
          type="submit"
          sx={actionButtonSx}
        >
          {buttonText}
        </Button>
      );

      if (disabled && disabledReason) {
        return (
          <Tooltip title={disabledReason} arrow placement="top" enterDelay={0} leaveDelay={100}>
            <span>{button}</span>
          </Tooltip>
        );
      }

      return button;
    }

    return (
      <Button onClick={onDepositClick} fullWidth size="large" variant="contained" sx={actionButtonSx}>
        {depositButtonText ||
          t({
            comment:
              'Deposit button fallback label. An action ("add funds"), not the noun for a deposit transaction — the uncontexted "Deposit" is the noun used by the account-history row and the table filter.',
            context: 'action',
            message: 'Deposit',
          })}
      </Button>
    );
  }
);

ActionButton.displayName = 'ActionButton';
